// apps/web/src/features/admin/form-config/hooks/useDebouncedSave.ts
import { useEffect, useRef } from 'react';
import { useFormConfigStore } from '../state/store';
import { formConfigApi } from '../api/client';

const DEBOUNCE_MS = 800;

export function useDebouncedSave(): void {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirty = useFormConfigStore((s) => s.dirty);
  const version = useFormConfigStore((s) => s.version);
  // F-032 — while conflicted, the autosave loop is suspended: retrying a
  // wholesale write from a stale copy is exactly the clobber this flag exists
  // to prevent. The workspace banner offers reload as the way out.
  const conflicted = useFormConfigStore((s) => s.conflicted);
  // Send contentV3 (the per-form source of truth) instead of the legacy
  // default-first flattened `content` view. The API accepts schemaVersion 1–3
  // verbatim (passthrough); version-service stores it unchanged; loadVersion
  // migrates it back to V3 on next load. Legacy drafts already load correctly.
  const contentV3 = useFormConfigStore((s) => s.contentV3);

  useEffect(() => {
    if (!dirty || !version || !contentV3 || conflicted || version.state !== 'draft') return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      useFormConfigStore.getState().markSaving();
      try {
        // F-032 — the write carries the revision this surface loaded; the
        // server rejects it (409 stale_draft) if another surface wrote the
        // draft in between, instead of letting this copy clobber theirs.
        const updated = await formConfigApi.updateDraft(
          version.id,
          contentV3,
          version.contentRevision,
        );
        useFormConfigStore.getState().markSaved(updated.contentRevision);
      } catch (e) {
        if (formConfigApi.isStaleDraftError(e)) {
          useFormConfigStore.getState().markConflicted();
          return;
        }
        useFormConfigStore.getState().markSaveFailed();
      }
    }, DEBOUNCE_MS);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [dirty, version, contentV3, conflicted]);
}
